import React from "react";
import { LightbulbIcon } from "@truevault/aquilo/src/components/selectMenu/SelectMenu";

/**
 * Shared help-card shell for the Data Flows pages, matching the pol-7592 vendor inventory
 * onboarding card (hairline border + subtle shadow, 16/600 title over 14px muted body).
 * Colors are inline hex because these token utility classes aren't in the compiled
 * Tailwind bundle.
 */
export function DataFlowsHelpCard({
  title,
  body,
  children,
}: {
  title: React.ReactNode;
  body?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        borderRadius: 12,
        border: "1px solid #E8E5EE",
        boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05)",
        backgroundColor: "#fff",
        padding: 24,
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 600, color: "#1A1726" }}>{title}</div>
      {body && (
        <div style={{ marginTop: 4, fontSize: 14, lineHeight: "21px", color: "#726D83" }}>
          {body}
        </div>
      )}
      {children}
    </div>
  );
}

export function SourcesExplanatoryCard({ isHr = false }: { isHr?: boolean }) {
  return (
    <DataFlowsHelpCard
      title="Where is your data sourced from"
      body="The source of personal data is the original point of origin for the information your business collects. Here are two common examples:"
    >
      <div style={{ marginTop: 16 }}>
        <div className="flex gap-[36px]">
          <div className="flex">
            <div className="rounded-[100px] text-[14px] text-neutral-700 bg-neutral-200 w-[21px] h-[21px] flex flex-col justify-center items-center">
              1
            </div>
            <div className="ml-[12px]">
              <div className="text-[16px] font-medium leading-[24px] text-neutral-900">
                {isHr ? "Workers as the Primary Source" : "Consumers as the Primary Source"}
              </div>
              <div className="text-[14px] leading-[21px] text-neutral-600 max-w-[60ch]">
                {isHr ? (
                  <>
                    For most businesses, workers are the primary source of employment data. This
                    happens directly when employees, contractors, or job applicants provide
                    information such as contact details, resumes, tax forms, or bank account
                    information.
                  </>
                ) : (
                  <>
                    For most businesses, the consumer is the primary source. This happens directly
                    (e.g., filling out a checkout form) or indirectly (e.g., website tracking). Even
                    if you use a platform like Shopify to collect this data, the consumer remains
                    the source—Shopify is simply the tool that retrieves and stores it for you.
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex">
            <div className="rounded-[100px] text-[14px] text-neutral-700 bg-neutral-200 w-[21px] h-[21px] flex flex-col justify-center items-center">
              2
            </div>
            <div className="ml-[12px]">
              <div className="text-[16px] font-medium leading-[24px] text-neutral-900">
                Third-Party Sources
              </div>
              <div className="text-[14px] leading-[21px] text-neutral-600 max-w-[60ch]">
                {isHr
                  ? "If an employment agency, recruiter, background check provider, or data broker provides employment data, that third party is the source for your business. "
                  : "If you purchase a marketing list containing names or emails, the consumer is not the source for your business. Instead, the source is the third-party company that sold you the list. "}
                <a
                  href="https://knowledge.truevault.com/article/195-determining-your-collection-sources"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-600 hover:text-primary-700 underline font-medium"
                >
                  Read more about Third-Party Sources
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DataFlowsHelpCard>
  );
}

export function CollectionExplanatoryCard({
  collectionGroupName,
  isHr,
  showCollectionForOtherBusinesses = false,
  showRecommendationGuidance = false,
}: {
  collectionGroupName: string;
  isHr: boolean;
  showCollectionForOtherBusinesses?: boolean;
  showRecommendationGuidance?: boolean;
}) {
  return (
    <DataFlowsHelpCard
      title={`What information do you collect about ${collectionGroupName}?`}
      body={
        <>
          Select the categories of personal information that you collect about {collectionGroupName}
          . Include all data handled by vendors or third parties.
        </>
      }
    >
      {showCollectionForOtherBusinesses && (
        <div style={{ marginTop: 12, fontSize: 14, lineHeight: "21px", color: "#726D83" }}>
          Include any personal information your business collects about consumers as a third party
          (e.g., purchasing data from data brokers, or collecting data from retailers for
          drop-shipping).
        </div>
      )}
      {!isHr && (
        <div
          style={{
            marginTop: 12,
            fontSize: 14,
            fontWeight: 500,
            lineHeight: "21px",
            color: "#6E50D7",
          }}
        >
          Don't include data collected from employees or job applicants here. HR collection is
          covered in a separate step.
        </div>
      )}
      {/* Recommendation guidance lives inside this card rather than as its own banner, so the
          page opens with a single block of guidance. */}
      {showRecommendationGuidance && (
        <div
          className="flex items-start gap-[8px]"
          style={{
            marginTop: 16,
            borderRadius: 6,
            padding: "10px 12px",
            backgroundColor: "#F3F0FE",
            fontSize: 14,
            lineHeight: "21px",
            color: "#3C3489",
          }}
        >
          <span className="text-primary-600 flex-shrink-0">{LightbulbIcon}</span>
          <span>
            {isHr
              ? // HR recommendations come from the vendor inventory only — the cross-org snapshot
                // is consumer-derived, so don't promise "similar businesses" here.
                "Categories we recommend based on the vendors you use are highlighted in purple. Use “Check all recommended” on a section, or hover a lightbulb to see why a category is recommended. Nothing is selected until you choose it."
              : "Categories we recommend for your business are highlighted in purple. Use “Check all recommended” on a section, or hover a lightbulb to see why a category is recommended. Nothing is selected until you choose it."}
          </span>
        </div>
      )}
    </DataFlowsHelpCard>
  );
}

export function DisclosuresExplanatoryCard({ isHr }: { isHr: boolean }) {
  return (
    <DataFlowsHelpCard
      title={`What categories of information are collected, analyzed by, stored in, or otherwise processed by your ${
        isHr ? "HR Data Recipients" : "Data Recipients"
      }?`}
      body="For each of your Data Recipients, identify which categories of personal data they process. This includes any categories of digital information that may be collected and processed automatically, as well as any information that your Vendors or Agencies have access to view."
    />
  );
}

export function HrPurposesExplanatoryCard({
  collectionGroupName,
}: {
  collectionGroupName: string;
}) {
  return (
    <DataFlowsHelpCard
      title={`How is personal information about ${collectionGroupName} used?`}
      body="Tell us whether your business uses this employment group's personal information for commercial purposes. Answer yes if you actively market to this group, such as by adding them to marketing lists. This helps determine which notices and disclosures apply."
    />
  );
}

export function PurposesExplanatoryCard({ showGdprLawfulBases }: { showGdprLawfulBases: boolean }) {
  return (
    <DataFlowsHelpCard
      title={
        showGdprLawfulBases
          ? "Which purposes and lawful bases are you using the collected data for?"
          : "Which purposes are you using the collected data for?"
      }
      body="Based on your data map, we have identified the following processing purposes. These purposes will be listed on your Privacy Policy shown on your site. Add any additional purposes or remove purposes that are no longer relevant."
    >
      <div
        style={{
          marginTop: 12,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: "21px",
          color: "#6E50D7",
        }}
      >
        Tip: Include any data processing performed by your Data Recipients on your business's
        behalf. For example, if you have a vendor that processes payments on your behalf, select
        Processing Payments below.
      </div>
      {showGdprLawfulBases && (
        <>
          <div style={{ marginTop: 12, fontSize: 14, lineHeight: "21px", color: "#726D83" }}>
            The GDPR allows businesses to collect and process data only for legitimate, lawful
            purposes called Lawful Bases.
            <br />
            These are the basic Lawful Bases:
          </div>
          <ul
            className="list-disc pl-[32px]"
            style={{ marginTop: 8, fontSize: 14, lineHeight: "21px", color: "#726D83" }}
          >
            <li>
              <span style={{ color: "#6E50D7", fontWeight: 500 }}>
                Complying with legal obligations:
              </span>{" "}
              Data is processed in order to meet legal or regulatory obligations. For example,
              complying with tax or privacy laws. You use TrueVault Polaris, so this one is easy!
            </li>
            <li>
              <span style={{ color: "#6E50D7", fontWeight: 500 }}>Fulfilling contracts:</span> Data
              is processed to honor a promise or commitment to the individual whose data you are
              processing. For example, fulfilling customer orders.
            </li>
            <li>
              <span style={{ color: "#6E50D7", fontWeight: 500 }}>Legitimate interests:</span> Data
              is processed for a business operational purpose, such as for sales, marketing, data
              storage, customer support, website management, or improving products/services, among
              others.
            </li>
            <li>
              <span style={{ color: "#6E50D7", fontWeight: 500 }}>Consent:</span> Consent is the
              lawful basis for certain processing activities, like data collection via website
              cookies and a few other less common processing activities.
            </li>
          </ul>
          <div style={{ marginTop: 8, fontSize: 14, lineHeight: "21px", color: "#726D83" }}>
            We've mapped Lawful Bases based on your data collection and vendors. Add or remove as
            needed.
          </div>
        </>
      )}
    </DataFlowsHelpCard>
  );
}
